import React, { useState, useMemo, useRef } from "react";
import axios from "axios";
import { showToast } from "../../utils/customToast";
import { useCompany } from "../../context/CompanyContext";
import {
  FaUpload,
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimes,
  FaRedo,
  FaTable,
  FaCheck,
  FaInfoCircle,
} from "react-icons/fa";
import "./ImportLedger.css";

const BASE_API_URL =
  (process.env.REACT_APP_BASE_API_URL || "http://localhost:5000/api").replace(
    /\/api\/?$/,
    ""
  ) + "/api";

// Schema Target Fields in HEAD_MAST
const TARGET_FIELDS = [
  { key: "HEAD_CODE", label: "HEAD_CODE", mandatory: true },
  { key: "GroupCODE", label: "GroupCODE", mandatory: true },
  { key: "HEADName", label: "HEADName", mandatory: false },
  { key: "OpeningBal", label: "OpeningBal", mandatory: false },
  { key: "OpenBlType", label: "OpenBlType", mandatory: false },
  { key: "BillbyBill", label: "BillbyBill", mandatory: false },
  { key: "CL_BALANCE", label: "CL_BALANCE", mandatory: false },
  { key: "ADDRESS1", label: "ADDRESS1", mandatory: false },
  { key: "ADDRESS2", label: "ADDRESS2", mandatory: false },
  { key: "PHONE", label: "PHONE", mandatory: false },
  { key: "MOBILE", label: "MOBILE", mandatory: false },
  { key: "PANNO", label: "PANNO", mandatory: false },
  { key: "EMAIL", label: "EMAIL", mandatory: false },
  { key: "GSTNO", label: "GSTNO", mandatory: false },
  { key: "StateID", label: "StateID", mandatory: false },
  { key: "CompID", label: "CompID", mandatory: false },
];

export default function ImportLedger() {
  const { selectedCompany } = useCompany();
  const fileInputRef = useRef(null);

  // Active Company ID
  const companyId = useMemo(() => {
    if (selectedCompany?.id || selectedCompany?.CompID) {
      return selectedCompany.id || selectedCompany.CompID;
    }
    const saved = localStorage.getItem("selectedCompany");
    if (saved) {
      try {
        const obj = JSON.parse(saved);
        return obj.id || obj.CompID || 1147;
      } catch (e) {}
    }
    return localStorage.getItem("companyId") || 1147;
  }, [selectedCompany]);

  // File & Parsing State
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [detectedHeaders, setDetectedHeaders] = useState([]);
  const [detectedMappings, setDetectedMappings] = useState({});
  const [allParsedRows, setAllParsedRows] = useState([]);
  const [isDragActive, setIsDragActive] = useState(false);

  // Upload Progress & Percentage Animation State
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [progressStatusMsg, setProgressStatusMsg] = useState("");

  // Result Summary State
  const [importResult, setImportResult] = useState(null);

  // Clean string cells (handles ="string", embedded quotes, "string", 'string', and whitespace)
  const cleanCell = (cell) => {
    if (cell == null) return "";
    let val = String(cell).trim();
    // Strip Excel export style ="value"
    if (val.startsWith('="') && val.endsWith('"')) {
      val = val.substring(2, val.length - 1);
    }
    // Remove all double quotes and smart double quotes (equivalent to REPLACE(..., '"', ''))
    val = val.replace(/["“”]/g, "");
    // Strip any leading and trailing single quotes, backticks or whitespace
    val = val.replace(/^['`\s]+|['`\s]+$/g, "");
    return val.trim();
  };

  // Safe row parser supporting comma and semicolon delimiters with quotes
  const parseLine = (line, sep) => {
    if (sep === "\t" || sep === "|") {
      return line.split(sep).map(cleanCell);
    }

    const tokens = [];
    let curr = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"' || ch === "'") {
        inQuotes = !inQuotes;
        curr += ch;
      } else if (ch === sep && !inQuotes) {
        tokens.push(cleanCell(curr));
        curr = "";
      } else {
        curr += ch;
      }
    }
    tokens.push(cleanCell(curr));
    return tokens;
  };

  // Header-based Auto-Detection (Option A: Automatically detects which column is which)
  const matchHeadersToFields = (headers) => {
    const mappings = {};
    const normHeaders = headers.map((h) =>
      cleanCell(h).toLowerCase().replace(/[^a-z0-9]/g, "")
    );

    TARGET_FIELDS.forEach((target) => {
      let matchedIndex = -1;

      for (let i = 0; i < normHeaders.length; i++) {
        const h = normHeaders[i];
        if (
          target.key === "HEAD_CODE" &&
          (h === "headcode" ||
            h === "code" ||
            h === "head" ||
            h === "accountcode" ||
            h === "headno" ||
            h === "acccode")
        ) {
          matchedIndex = i;
          break;
        } else if (
          target.key === "GroupCODE" &&
          (h === "groupcode" ||
            h === "group" ||
            h === "grpcode" ||
            h === "groupname" ||
            h === "grp")
        ) {
          matchedIndex = i;
          break;
        } else if (
          target.key === "HEADName" &&
          (h === "headname" ||
            h === "ledgername" ||
            h === "name" ||
            h === "ledger" ||
            h === "accountname" ||
            h === "particulars")
        ) {
          matchedIndex = i;
          break;
        } else if (
          target.key === "OpeningBal" &&
          (h === "openingbal" ||
            h === "openingbalance" ||
            h === "openbal" ||
            h === "balance" ||
            h === "opbal")
        ) {
          matchedIndex = i;
          break;
        } else if (
          target.key === "OpenBlType" &&
          (h === "openbltype" ||
            h === "bltype" ||
            h === "drcr" ||
            h === "type" ||
            h === "opbltype")
        ) {
          matchedIndex = i;
          break;
        } else if (
          target.key === "BillbyBill" &&
          (h === "billbybill" || h === "bill" || h === "billwise")
        ) {
          matchedIndex = i;
          break;
        } else if (
          target.key === "CL_BALANCE" &&
          (h === "clbalance" ||
            h === "closingbalance" ||
            h === "closebal" ||
            h === "closingbal")
        ) {
          matchedIndex = i;
          break;
        } else if (
          target.key === "ADDRESS1" &&
          (h === "address1" || h === "address" || h === "addr1" || h === "add1")
        ) {
          matchedIndex = i;
          break;
        } else if (
          target.key === "ADDRESS2" &&
          (h === "address2" || h === "addr2" || h === "add2")
        ) {
          matchedIndex = i;
          break;
        } else if (
          target.key === "MOBILE" &&
          (h === "mobile" || h === "cell" || h === "contact" || h === "mob")
        ) {
          matchedIndex = i;
          break;
        } else if (
          target.key === "PHONE" &&
          (h === "phone" || h === "telephone" || h === "tel")
        ) {
          matchedIndex = i;
          break;
        } else if (
          target.key === "PANNO" &&
          (h === "panno" || h === "pan" || h === "pancard" || h === "pannumber")
        ) {
          matchedIndex = i;
          break;
        } else if (
          target.key === "EMAIL" &&
          (h === "email" || h === "mail" || h === "emailid" || h === "emailaddress")
        ) {
          matchedIndex = i;
          break;
        } else if (
          target.key === "GSTNO" &&
          (h === "gstno" || h === "gst" || h === "gstin" || h === "gstnumber")
        ) {
          matchedIndex = i;
          break;
        } else if (
          target.key === "StateID" &&
          (h === "stateid" || h === "state" || h === "statecode")
        ) {
          matchedIndex = i;
          break;
        } else if (
          target.key === "CompID" &&
          (h === "compid" || h === "companyid" || h === "company")
        ) {
          matchedIndex = i;
          break;
        }
      }

      if (matchedIndex !== -1) {
        mappings[target.key] = matchedIndex;
      }
    });

    return mappings;
  };

  // Process uploaded file
  const processFile = (file) => {
    setFileError("");
    if (!file) return;

    const ext = file.name.split(".").pop().toLowerCase();
    if (ext !== "csv" && ext !== "txt") {
      const errorMsg = `Not Allowed! Only .csv and .txt files are supported. (Selected file: .${ext})`;
      setFileError(errorMsg);
      showToast.error(errorMsg);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    setSelectedFile(file);
    setImportResult(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text
        .split(/\r\n|\n|\r/)
        .map((l) => l.trim())
        .filter((l) => l.length > 0);

      if (lines.length === 0) {
        showToast.error("The selected file is empty.");
        return;
      }

      // Automatically determine separator
      let sep = ",";
      const firstLine = lines[0];
      if (ext === "txt") {
        if (firstLine.includes(";")) {
          sep = ";";
        } else if (firstLine.includes("\t")) {
          sep = "\t";
        } else if (firstLine.includes("|")) {
          sep = "|";
        } else {
          sep = ";";
        }
      } else {
        // .csv defaults to comma
        sep = firstLine.includes(";") && !firstLine.includes(",") ? ";" : ",";
      }

      // Option A: First row is ALWAYS the header row
      const headerRow = parseLine(firstLine, sep);
      setDetectedHeaders(headerRow);

      // Auto-detect columns
      const mappings = matchHeadersToFields(headerRow);
      setDetectedMappings(mappings);

      // Parse data rows (ignoring completely blank rows)
      const dataRows = [];
      for (let i = 1; i < lines.length; i++) {
        const tokens = parseLine(lines[i], sep);
        const hasContent = tokens.some((cell) => cell !== "");
        if (hasContent) {
          dataRows.push(tokens);
        }
      }

      setAllParsedRows(dataRows);

      // Validation check for mandatory headers
      if (mappings.HEAD_CODE === undefined || mappings.GroupCODE === undefined) {
        showToast.warn(
          "Mandatory headers missing: Could not find 'HEAD_CODE' or 'GroupCODE' in your file header."
        );
      } else {
        showToast.success(
          `Headers auto-detected successfully (${Object.keys(mappings).length} columns matched)`
        );
      }
    };

    reader.onerror = () => {
      showToast.error("Failed to read file.");
    };

    reader.readAsText(file);
  };

  // Reset file selection
  const handleReset = () => {
    setSelectedFile(null);
    setFileError("");
    setDetectedHeaders([]);
    setDetectedMappings({});
    setAllParsedRows([]);
    setImportResult(null);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Smooth Percentage Animator
  const animatePercentage = (from, to, durationMs, onDone) => {
    const startTime = performance.now();
    const step = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / durationMs, 1);
      const currentVal = Math.round(from + (to - from) * progress);
      setUploadProgress(currentVal);
      if (progress < 1) {
        requestAnimationFrame(step);
      } else if (onDone) {
        onDone();
      }
    };
    requestAnimationFrame(step);
  };

  // Start Import
  const handleStartImport = async () => {
    if (!companyId) {
      showToast.error("No company selected. Please select a company first.");
      return;
    }

    if (!selectedFile || allParsedRows.length === 0) {
      showToast.error("Please select a valid CSV or TXT file with records.");
      return;
    }

    // Check mandatory headers
    if (detectedMappings.HEAD_CODE === undefined) {
      showToast.error("Header 'HEAD_CODE' was not found in your file.");
      return;
    }
    if (detectedMappings.GroupCODE === undefined) {
      showToast.error("Header 'GroupCODE' was not found in your file.");
      return;
    }

    // Build mapped rows payload
    const mappedRows = [];
    for (let i = 0; i < allParsedRows.length; i++) {
      const rowArr = allParsedRows[i];
      const record = {};
      TARGET_FIELDS.forEach((field) => {
        const colIdx = detectedMappings[field.key];
        if (colIdx !== undefined && rowArr[colIdx] !== undefined) {
          let val = rowArr[colIdx];
          if (typeof val === "string") {
            // Trim all double quotes from address, head name, etc.
            val = val.replace(/["“”]/g, "").trim();
          }
          record[field.key] = val;
        }
      });
      mappedRows.push(record);
    }

    setUploading(true);
    setUploadProgress(0);
    setProgressStatusMsg("Reading headers & validating rows...");

    // Smooth stage 1 animation: 0% -> 35%
    animatePercentage(0, 35, 300, () => {
      setProgressStatusMsg("Checking existing Head Codes and validating mandatory fields...");
    });

    try {
      setTimeout(() => {
        animatePercentage(35, 75, 400, () => {
          setProgressStatusMsg("Inserting ledgers into HEAD_MAST table...");
        });
      }, 350);

      const response = await axios.post(`${BASE_API_URL}/ledger/import`, {
        CompID: companyId,
        rows: mappedRows,
      });

      // Complete percentage animation: 75% -> 100%
      animatePercentage(75, 100, 300, () => {
        setProgressStatusMsg("Import completed successfully!");
        setTimeout(() => {
          setUploading(false);
          if (response.data && response.data.success) {
            setImportResult(response.data);
            showToast.success(
              `Import finished! Added: ${response.data.summary.insertedCount}, Skipped: ${
                response.data.summary.skippedDuplicateCount + response.data.summary.skippedInvalidCount
              }`
            );
          } else {
            showToast.error(response.data?.error || "Import failed");
          }
        }, 300);
      });
    } catch (err) {
      setUploading(false);
      setUploadProgress(0);
      console.error("Import Error:", err);
      const errMsg = err.response?.data?.error || err.message || "Failed to import ledgers";
      showToast.error(errMsg);
    }
  };

  const isMandatoryMatched =
    detectedMappings.HEAD_CODE !== undefined && detectedMappings.GroupCODE !== undefined;

  return (
    <div className="import-ledger-wrapper">
      <div className="import-container">
        {/* ========================================================
            CARD 1: FILE UPLOAD
            ======================================================== */}
        <div className="import-card">
          <div className="import-card-header">
            <div className="import-card-title-group">
              <div className="import-card-icon-wrap teal-icon">
                <FaUpload />
              </div>
              <div>
                <h2 className="import-card-title">Upload Ledger File (.csv or .txt)</h2>
                <p className="import-card-desc">
                  Header names are automatically detected regardless of column order.
                </p>
              </div>
            </div>

            <div className="import-header-actions">
              {selectedFile && (
                <button
                  type="button"
                  className="import-btn import-btn-outline"
                  onClick={handleReset}
                  title="Clear selected file"
                >
                  <FaRedo /> Clear
                </button>
              )}
            </div>
          </div>

          <div className="import-card-body">
            {fileError && (
              <div className="detect-status-alert error" style={{ marginBottom: "8px" }}>
                <FaExclamationTriangle />
                <span>{fileError}</span>
              </div>
            )}
            {!selectedFile ? (
              <div
                className={`import-dropzone ${isDragActive ? "drag-active" : ""}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragActive(true);
                }}
                onDragLeave={() => setIsDragActive(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDragActive(false);
                  if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                    processFile(e.dataTransfer.files[0]);
                  }
                }}
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.txt"
                  className="import-file-input-hidden"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      processFile(e.target.files[0]);
                    }
                  }}
                />
                <FaUpload className="import-dropzone-icon" />
                <p className="import-dropzone-text">
                  Click to browse or drag and drop your <b>.CSV</b> or <b>.TXT</b> file here
                </p>
                <p className="import-dropzone-hint">
                  Header-based auto detection: columns are matched by name in any order. Only .csv and .txt accepted.
                </p>
              </div>
            ) : (
              <div>
                {/* File Selected Badge */}
                <div className="import-file-info-bar">
                  <div className="import-file-info-left">
                    <span className="import-file-badge">
                      {selectedFile.name.endsWith(".txt") ? "TXT" : "CSV"}
                    </span>
                    <span className="import-file-name">{selectedFile.name}</span>
                    <span className="import-file-meta">
                      ({(selectedFile.size / 1024).toFixed(1)} KB &bull; {allParsedRows.length} data rows)
                    </span>
                  </div>
                  <button
                    type="button"
                    className="import-file-remove-btn"
                    onClick={handleReset}
                    title="Remove file"
                  >
                    <FaTimes />
                  </button>
                </div>

                {/* Auto Detection Status Banner */}
                {isMandatoryMatched ? (
                  <div className="detect-status-alert success">
                    <FaCheck />
                    <span>
                      Headers Auto-Detected: Mandatory fields <b>HEAD_CODE</b> (Col{" "}
                      {detectedMappings.HEAD_CODE + 1}) and <b>GroupCODE</b> (Col{" "}
                      {detectedMappings.GroupCODE + 1}) matched successfully with{" "}
                      {Object.keys(detectedMappings).length} total columns.
                    </span>
                  </div>
                ) : (
                  <div className="detect-status-alert error">
                    <FaExclamationTriangle />
                    <span>
                      Missing Mandatory Headers: File must have headers matching <b>HEAD_CODE</b> and{" "}
                      <b>GroupCODE</b>.
                    </span>
                  </div>
                )}

                {/* Detected Column Badges */}
                <div className="detected-pills-container">
                  {TARGET_FIELDS.map((field) => {
                    const colIdx = detectedMappings[field.key];
                    const isMatched = colIdx !== undefined;
                    if (field.mandatory) {
                      return (
                        <span
                          key={field.key}
                          className={`detected-pill ${
                            isMatched ? "mandatory" : "mandatory-missing"
                          }`}
                        >
                          {isMatched ? <FaCheck /> : <FaTimes />}
                          {field.label}
                          {isMatched && <span className="detected-pill-pos">Col {colIdx + 1}</span>}
                        </span>
                      );
                    }
                    if (isMatched) {
                      return (
                        <span key={field.key} className="detected-pill">
                          {field.label}
                          <span className="detected-pill-pos">Col {colIdx + 1}</span>
                        </span>
                      );
                    }
                    return null;
                  })}
                </div>

                {/* Start Import Action Button */}
                {!uploading && !importResult && (
                  <div className="import-actions-bar">
                    <button
                      type="button"
                      className="import-btn import-btn-primary"
                      onClick={handleStartImport}
                      disabled={!isMandatoryMatched}
                      title={
                        isMandatoryMatched
                          ? "Proceed to import ledgers into HEAD_MAST"
                          : "Both HEAD_CODE and GroupCODE headers must be detected to import"
                      }
                    >
                      <FaCheck /> Start Ledger Import
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ========================================================
            PERCENTAGE PROGRESS ANIMATION CARD (WHEN UPLOADING)
            ======================================================== */}
        {uploading && (
          <div className="import-progress-card">
            <div className="import-progress-header">
              <h3 className="import-progress-title">
                <FaUpload className="config-title-icon" /> Uploading &amp; Importing Ledgers...
              </h3>
              <span className="import-percentage-badge">{uploadProgress}%</span>
            </div>

            <div className="import-progress-track">
              <div
                className="import-progress-bar"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>

            <p className="import-progress-status-msg">
              <FaInfoCircle style={{ marginRight: "4px" }} />
              {progressStatusMsg}
            </p>
          </div>
        )}

        {/* ========================================================
            CARD 2: RESULT SUMMARY (AFTER IMPORT)
            ======================================================== */}
        {importResult && (
          <div className="import-card">
            <div className="import-card-header">
              <div className="import-card-title-group">
                <div className="import-card-icon-wrap teal-icon">
                  <FaCheckCircle />
                </div>
                <div>
                  <h2 className="import-card-title">Import Summary &amp; Results</h2>
                  <p className="import-card-desc">
                    {importResult.message || "Ledger import batch completed."}
                  </p>
                </div>
              </div>

              <div className="import-header-actions">
                <button
                  type="button"
                  className="import-btn import-btn-outline"
                  onClick={handleReset}
                >
                  <FaRedo /> Import Another File
                </button>
              </div>
            </div>

            <div className="import-card-body">
              {/* Metric Statistics Cards */}
              <div className="summary-stats-grid">
                <div className="stat-metric-card total">
                  <div className="stat-icon-wrap">
                    <FaTable />
                  </div>
                  <div className="stat-data">
                    <span className="stat-val">{importResult.summary?.totalRows || 0}</span>
                    <span className="stat-lbl">Total Rows in File</span>
                  </div>
                </div>

                <div className="stat-metric-card success">
                  <div className="stat-icon-wrap">
                    <FaCheckCircle />
                  </div>
                  <div className="stat-data">
                    <span className="stat-val">{importResult.summary?.insertedCount || 0}</span>
                    <span className="stat-lbl">Successfully Added</span>
                  </div>
                </div>

                <div className="stat-metric-card duplicate">
                  <div className="stat-icon-wrap">
                    <FaInfoCircle />
                  </div>
                  <div className="stat-data">
                    <span className="stat-val">
                      {importResult.summary?.skippedDuplicateCount || 0}
                    </span>
                    <span className="stat-lbl">Skipped (Already Exists)</span>
                  </div>
                </div>

                <div className="stat-metric-card invalid">
                  <div className="stat-icon-wrap">
                    <FaExclamationTriangle />
                  </div>
                  <div className="stat-data">
                    <span className="stat-val">
                      {importResult.summary?.skippedInvalidCount || 0}
                    </span>
                    <span className="stat-lbl">Skipped (Missing Fields)</span>
                  </div>
                </div>
              </div>

              {/* Table: Only rows skipped because of missing fields */}
              {(() => {
                const missingItems =
                  importResult.details?.skippedInvalid ||
                  (importResult.details?.skipped || []).filter(
                    (item) =>
                      item.reason &&
                      !item.reason.toLowerCase().includes("already exists") &&
                      !item.reason.toLowerCase().includes("duplicate")
                  );

                if (!missingItems || missingItems.length === 0) return null;

                return (
                  <div style={{ marginTop: "12px" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: "6px",
                      }}
                    >
                      <label
                        style={{
                          fontSize: "11px",
                          fontWeight: 700,
                          color: "#991b1b",
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                        }}
                      >
                        <FaExclamationTriangle /> Skipped Records (Missing Required Fields - {missingItems.length})
                      </label>
                    </div>
                    <div className="skipped-table-container">
                      <table className="skipped-table">
                        <thead>
                          <tr>
                            <th style={{ width: "50px" }}>Row #</th>
                            <th style={{ width: "95px" }}>Head Code</th>
                            <th>Ledger Name</th>
                            <th>Reason / Missing Field</th>
                          </tr>
                        </thead>
                        <tbody>
                          {missingItems.map((item, idx) => (
                            <tr key={idx}>
                              <td style={{ fontWeight: 600 }}>{item.row || idx + 1}</td>
                              <td style={{ fontFamily: "monospace", fontWeight: 700 }}>
                                {item.headCode ? (
                                  item.headCode
                                ) : (
                                  <span style={{ color: "#ef4444" }}>[Missing]</span>
                                )}
                              </td>
                              <td>{item.name || "N/A"}</td>
                              <td>
                                <span className="skipped-badge invalid">{item.reason}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
